import { useEffect, useRef, useState } from 'react';

/**
 * The self-correcting loop — a minimal, cinematic showcase. A single gorgeous
 * abstract "loop" clip plays full-bleed; the six stages of the loop advance
 * over it as you scroll, SpaceX-style, with a quiet step rail on the right.
 */
const STEPS = [
  {
    label: 'Plan',
    body:
      'Before a frame is rendered, Rhapsody works out what this shot needs to show and what it can never break.',
    detail: 'Planned in full story context',
  },
  {
    label: 'Generate',
    body:
      'The shot is handed to whichever generator suits it best. The tool is interchangeable; the intent is not.',
    detail: 'Model-agnostic',
  },
  {
    label: 'Verify',
    body:
      'Every candidate is judged against the story and against real cinematic craft. rigorously, not by feel.',
    detail: 'Held to a real standard',
  },
  {
    label: 'Diagnose',
    body:
      'When a shot misses, Rhapsody knows precisely what went wrong and why.',
    detail: 'Pinpoint failure analysis',
  },
  {
    label: 'Re-plan',
    body:
      'The fix feeds straight back into the plan. Only what failed is redone; everything that already worked stays.',
    detail: 'Self-correcting, not re-rolling',
  },
  {
    label: 'Approve',
    body:
      'The shot joins the cut and the story-world updates, so everything that follows builds on it.',
    detail: 'State updates. The loop continues.',
  },
];

type SurfacePoint = {
  px: number;
  py: number;
  nx: number;
  ny: number;
  nz: number;
};

function seededRandom(seed: number) {
  let state = seed >>> 0;
  return () => {
    state = (state * 1664525 + 1013904223) >>> 0;
    return state / 4294967296;
  };
}

// The visible pole leans toward the upper-left, exactly as in the reference.
const TILT_X = 0.68;
const TILT_Z = 0.455;
const COS_X = Math.cos(TILT_X);
const SIN_X = Math.sin(TILT_X);
const COS_Z = Math.cos(TILT_Z);
const SIN_Z = Math.sin(TILT_Z);

// Key light arrives from the upper-left, slightly in front of the sphere.
const LIGHT_LENGTH = Math.hypot(0.6, 0.56, 0.45);
const LIGHT_X = -0.6 / LIGHT_LENGTH;
const LIGHT_Y = 0.56 / LIGHT_LENGTH;
const LIGHT_Z = 0.45 / LIGHT_LENGTH;

const RING_LEVELS = 22;

// Contour lines run steel-gray in shadow and heat up to ember orange in light.
const RING_STOPS: Array<[number, number, number, number, number]> = [
  [0.0, 96, 99, 112, 0.14],
  [0.22, 136, 123, 116, 0.38],
  [0.45, 185, 134, 100, 0.58],
  [0.68, 240, 157, 97, 0.8],
  [0.86, 255, 197, 137, 0.95],
  [1.0, 255, 233, 200, 1.0],
];

const RING_STYLES = Array.from({ length: RING_LEVELS + 1 }, (_, level) => {
  const brightness = level / RING_LEVELS;
  let stop = 0;
  while (stop < RING_STOPS.length - 2 && RING_STOPS[stop + 1][0] < brightness) stop += 1;
  const [b0, r0, g0, bl0, a0] = RING_STOPS[stop];
  const [b1, r1, g1, bl1, a1] = RING_STOPS[stop + 1];
  const t = b1 === b0 ? 0 : Math.min(1, Math.max(0, (brightness - b0) / (b1 - b0)));
  const mix = (from: number, to: number) => from + (to - from) * t;
  return {
    style: `rgba(${Math.round(mix(r0, r1))}, ${Math.round(mix(g0, g1))}, ${Math.round(
      mix(bl0, bl1),
    )}, ${(mix(a0, a1) * 0.92).toFixed(3)})`,
    width: 0.5 + brightness * 0.7,
  };
});

const WARM_ARC = { glow: '#ff8a45', mid: '#ffab6d', core: '#ffdcb2' };
const COOL_ARC = { glow: '#4f7dff', mid: '#8fb2ff', core: '#e2ecff' };

type ArcSpec = {
  lat: number;
  mode: 'limb' | 'face';
  target: number;
  span: number;
  width: number;
  cool: boolean;
  alpha: number;
  glint: number;
};

// Luminous traces riding individual contour lines. `target` is the screen
// angle (0 = right, π/2 = up) where each trace should peak, matching the
// reference frame: a long ember sweep on the lower-right limb, blue filaments
// across the upper face, and a hot stacked cluster at the upper-left limb.
const ARC_SPECS: ArcSpec[] = [
  // long ember sweep along the lower-right limb
  { lat: -0.66, mode: 'limb', target: -0.88, span: 2.05, width: 1.7, cool: false, alpha: 0.95, glint: 0.85 },
  { lat: -0.8, mode: 'limb', target: -1.1, span: 1.3, width: 1.1, cool: false, alpha: 0.5, glint: 0 },
  // warm accent crossing the right limb near the equator line
  { lat: -0.32, mode: 'limb', target: 0.05, span: 1.15, width: 1.1, cool: false, alpha: 0.55, glint: 0.4 },
  // blue filaments across the upper face and at the limbs
  { lat: 0.58, mode: 'face', target: 0, span: 2.3, width: 1.05, cool: true, alpha: 0.62, glint: 0 },
  { lat: -0.06, mode: 'limb', target: 0.38, span: 1.0, width: 0.9, cool: true, alpha: 0.45, glint: 0.6 },
  { lat: 0.2, mode: 'limb', target: -2.95, span: 0.55, width: 0.85, cool: true, alpha: 0.4, glint: 0.5 },
  // hot stacked cluster on the upper-left limb — these latitudes cross the
  // silhouette between screen angles ~2.35 and ~2.95 given the sphere tilt
  { lat: 0.83, mode: 'limb', target: 2.35, span: 0.85, width: 1.3, cool: false, alpha: 0.95, glint: 0.95 },
  { lat: 0.76, mode: 'limb', target: 2.5, span: 0.7, width: 1.35, cool: false, alpha: 1, glint: 1 },
  { lat: 0.68, mode: 'limb', target: 2.65, span: 0.6, width: 1.2, cool: false, alpha: 0.85, glint: 0.8 },
  { lat: 0.6, mode: 'limb', target: 2.78, span: 0.55, width: 1.1, cool: false, alpha: 0.75, glint: 0.7 },
  { lat: 0.49, mode: 'limb', target: 2.95, span: 0.6, width: 1.05, cool: false, alpha: 0.6, glint: 0.55 },
  // faint warm arcs low on the left limb
  { lat: 0.0, mode: 'limb', target: -2.7, span: 0.8, width: 0.95, cool: false, alpha: 0.45, glint: 0.35 },
  { lat: -0.5, mode: 'limb', target: -2.1, span: 0.8, width: 0.95, cool: false, alpha: 0.35, glint: 0 },
];

// Dust banks behind the sphere, in units of its radius (screen y grows down):
// a bright plume hugging the lit limb, a broad diagonal band, a fading tail.
const DUST_CLOUDS = [
  { x: -0.92, y: -0.52, rx: 0.62, ry: 0.4, rot: -0.62, count: 820, boost: 1.6 },
  { x: -1.38, y: -0.16, rx: 1.05, ry: 0.62, rot: -0.5, count: 1500, boost: 1.05 },
  { x: -1.92, y: 0.34, rx: 1.32, ry: 0.86, rot: -0.42, count: 980, boost: 0.65 },
];

function angleDelta(a: number, b: number) {
  let delta = a - b;
  while (delta > Math.PI) delta -= Math.PI * 2;
  while (delta < -Math.PI) delta += Math.PI * 2;
  return delta;
}

/**
 * A canvas-rendered sphere rather than a rotating flat asset. The backdrop
 * (nebula, dust, body) renders once; the contour rings live on a second
 * layer that re-renders whenever the sphere's orientation moves — scroll
 * spins it, eases its tilt and swings the rim light — while comet sparks,
 * luminous arcs and glints animate on top every frame.
 */
function OrbitalPlanet({ active }: { active: number }) {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const context = canvas.getContext('2d');
    if (!context) return;

    const staticLayer = document.createElement('canvas');
    const staticContext = staticLayer.getContext('2d');
    if (!staticContext) return;

    const ringLayer = document.createElement('canvas');
    const ringContext = ringLayer.getContext('2d');
    if (!ringContext) return;

    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    let width = 1;
    let height = 1;
    let centerX = 0;
    let centerY = 0;
    let radius = 1;
    let animationFrame = 0;

    // Live orientation: the base tilt plus scroll-driven spin, a slow tumble
    // and a rim light that swings across the limb as the section advances.
    let cosX = COS_X;
    let sinX = SIN_X;
    let cosZ = COS_Z;
    let sinZ = SIN_Z;
    let spin = 0;
    let tiltXNow = TILT_X;
    let tiltZNow = TILT_Z;
    let lightSwing = 0;
    let energyLift = 0;
    let sectionProgress = 0;
    let renderedSpin = Infinity;
    let renderedTiltX = TILT_X;
    let renderedTiltZ = TILT_Z;
    let renderedSwing = 0;
    let renderedLift = 0;

    const setOrientation = (tiltX: number, tiltZ: number) => {
      cosX = Math.cos(tiltX);
      sinX = Math.sin(tiltX);
      cosZ = Math.cos(tiltZ);
      sinZ = Math.sin(tiltZ);
    };

    type Grain = {
      x: number;
      y: number;
      radius: number;
      alpha: number;
      phase: number;
      cool: boolean;
    };
    type Star = Grain & { warm: boolean };
    type Arc = ArcSpec & { lon0: number; phase: number };
    type Comet = {
      lat: number;
      lon: number;
      speed: number;
      tail: number;
      width: number;
      cool: boolean;
      alpha: number;
    };
    // Sparkles pinned to the surface in (lat, lon) so they rotate with it.
    type SparkNode = {
      lat: number;
      lon: number;
      radius: number;
      phase: number;
      cool: boolean;
    };
    type Ring = {
      lat: number;
      gain: number;
      a1: number;
      p1: number;
      a2: number;
      p2: number;
    };

    let stars: Star[] = [];
    let dustParticles: Grain[] = [];
    let dustSparks: Grain[] = [];
    let sparkNodes: SparkNode[] = [];
    let rings: Ring[] = [];
    let arcs: Arc[] = [];
    let comets: Comet[] = [];

    let lastTime = 0;
    let lastProgress = -1;
    let scrollEnergy = 0;

    const surface = (lat: number, lon: number): SurfacePoint => {
      const ringRadius = Math.cos(lat);
      const x = ringRadius * Math.cos(lon);
      const y = Math.sin(lat);
      const z = ringRadius * Math.sin(lon);
      const rotatedY = y * cosX - z * sinX;
      const rotatedZ = y * sinX + z * cosX;
      const rotatedX = x * cosZ - rotatedY * sinZ;
      const finalY = x * sinZ + rotatedY * cosZ;
      const perspective = 1 + rotatedZ / 7.2;
      return {
        px: centerX + rotatedX * radius * perspective,
        py: centerY - finalY * radius * perspective,
        nx: rotatedX,
        ny: finalY,
        nz: rotatedZ,
      };
    };

    // Per-point line brightness: broad key light from the upper-left, a hot
    // rim at the upper-left limb, a softer ember rim at the lower-right, and
    // near-black everywhere else — the lighting the reference frame shows.
    const shade = (point: SurfacePoint) => {
      const diffuse = Math.max(0, point.nx * LIGHT_X + point.ny * LIGHT_Y + point.nz * LIGHT_Z);
      const limb = Math.pow(Math.max(0, 1 - point.nz), 2.1);
      const angle = Math.atan2(point.ny, point.nx);
      const upperLeft = angleDelta(angle, 2.55 + lightSwing) / 0.72;
      const lowerRight = angleDelta(angle, -0.82 + lightSwing) / 0.58;
      const rim =
        limb * Math.exp(-upperLeft * upperLeft) * 0.92 +
        limb * Math.exp(-lowerRight * lowerRight) * 0.48;
      return Math.min(1, 0.07 + diffuse * 0.22 + rim);
    };

    const rebuildScene = () => {
      const random = seededRandom(2046);
      const gauss = () => {
        const u = Math.max(1e-6, random());
        const v = random();
        return Math.sqrt(-2 * Math.log(u)) * Math.cos(Math.PI * 2 * v);
      };

      stars = Array.from({ length: 210 }, () => ({
        x: random(),
        y: random(),
        radius: 0.25 + random() * 1.0,
        alpha: 0.05 + random() * 0.38,
        phase: random() * Math.PI * 2,
        cool: false,
        warm: random() > 0.2,
      }));

      dustParticles = [];
      dustSparks = [];
      for (const cloud of DUST_CLOUDS) {
        const cosR = Math.cos(cloud.rot);
        const sinR = Math.sin(cloud.rot);
        for (let i = 0; i < cloud.count; i += 1) {
          const gx = gauss() * 0.55;
          const gy = gauss() * 0.55;
          const localX = gx * cloud.rx;
          const localY = gy * cloud.ry;
          const x = centerX + (cloud.x + localX * cosR - localY * sinR) * radius;
          const y = centerY + (cloud.y + localX * sinR + localY * cosR) * radius;
          if (x < -20 || x > width + 20 || y < -20 || y > height + 20) continue;
          const edgeFade = Math.min(1, Math.max(0, (x / width - 0.12) / 0.16));
          if (edgeFade <= 0) continue;
          const falloff = Math.exp(-(gx * gx + gy * gy) * 0.55);
          const roll = random();
          const bright = roll < 0.075;
          const grain: Grain = {
            x,
            y,
            radius: bright ? 0.7 + random() * 1.05 : 0.3 + random() * 0.75,
            alpha: Math.min(
              0.85,
              (0.06 + random() * 0.34) * falloff * cloud.boost * edgeFade * (bright ? 2.4 : 1),
            ),
            phase: random() * Math.PI * 2,
            cool: roll > 0.968,
          };
          const insideSphere = Math.hypot(x - centerX, y - centerY) < radius + 1.5;
          if (!insideSphere && random() < 0.07) {
            dustSparks.push({ ...grain, alpha: Math.min(0.9, grain.alpha * 1.8 + 0.12) });
          } else {
            dustParticles.push(grain);
          }
        }
      }

      // Unevenly spaced latitudes read as organic grooves, not a plot grid.
      // Each ring also carries two low-order brightness harmonics anchored to
      // the surface — the "etching" that slides along the line as the sphere
      // spins, which is what makes the rotation legible on a line-globe.
      rings = [];
      let lat = -1.52;
      while (lat <= 1.5) {
        rings.push({
          lat,
          gain: 0.6 + random() * 0.55 + (random() < 0.08 ? 0.3 : 0),
          a1: 0.09 + random() * 0.14,
          p1: random() * Math.PI * 2,
          a2: 0.05 + random() * 0.11,
          p2: random() * Math.PI * 2,
        });
        lat += 0.028 + random() * 0.012 + (random() < 0.08 ? 0.03 : 0);
      }

      // Anchor each luminous trace at the longitude whose screen position
      // best matches its target angle on the limb (or the face centre).
      arcs = ARC_SPECS.map((spec, index) => {
        let bestScore = -1;
        let lon0 = 0;
        for (let sample = 0; sample < 720; sample += 1) {
          const lon = (sample / 720) * Math.PI * 2;
          const point = surface(spec.lat, lon);
          if (point.nz < 0) continue;
          const offset = angleDelta(Math.atan2(point.ny, point.nx), spec.target) / 0.4;
          const score =
            spec.mode === 'face'
              ? point.nz
              : Math.pow(Math.max(0, 1 - point.nz), 1.6) * Math.exp(-offset * offset);
          if (score > bestScore) {
            bestScore = score;
            lon0 = lon;
          }
        }
        return { ...spec, lon0, phase: index * 1.7 };
      });

      // Comet sparks riding the contour grooves. `lon` is a MATERIAL longitude
      // fixed to the surface — the globe's spin carries them (via + spin in
      // drawComet), so they stay locked to their ring like markings painted on
      // the sphere. `speed` is only their own gentle orbital drift along that
      // ring; the limb fade makes each surface on one side, streak across the
      // face, and slip away behind the silhouette.
      const COMET_LATS = [-1.14, -0.9, -0.58, -0.3, -0.04, 0.22, 0.48, 0.72, 0.98];
      comets = COMET_LATS.map((cometLat) => ({
        lat: cometLat + (random() - 0.5) * 0.05,
        lon: random() * Math.PI * 2,
        speed: 0.05 + random() * 0.07,
        tail: 0.55 + random() * 0.6,
        width: 0.8 + random() * 0.7,
        cool: random() < 0.32,
        alpha: 0.45 + random() * 0.4,
      }));

      // Pinpoint sparkles that sit on the contour lines, favouring lit areas.
      // Stored in surface coordinates so they ride the rotation, twinkling
      // across the face and slipping behind the limb like ground beacons.
      sparkNodes = [];
      let guard = 0;
      while (sparkNodes.length < 30 && guard < 700) {
        guard += 1;
        const ring = rings[Math.floor(random() * rings.length)];
        const lon = random() * Math.PI * 2;
        const point = surface(ring.lat, lon + spin);
        if (point.nz < 0.06) continue;
        if (shade(point) < 0.2 && random() > 0.3) continue;
        sparkNodes.push({
          lat: ring.lat,
          lon,
          radius: 0.5 + random() * 0.9,
          phase: random() * Math.PI * 2,
          cool: random() > 0.88,
        });
      }
    };

    const renderBackdrop = () => {
      staticContext.clearRect(0, 0, width, height);
      staticContext.lineCap = 'round';
      staticContext.lineJoin = 'round';

      // Soft nebula glow under the dust banks.
      const glow = (gx: number, gy: number, gr: number, color: string) => {
        const cx = centerX + gx * radius;
        const cy = centerY + gy * radius;
        const gradient = staticContext.createRadialGradient(cx, cy, 0, cx, cy, gr * radius);
        gradient.addColorStop(0, color);
        gradient.addColorStop(1, 'rgba(0, 0, 0, 0)');
        staticContext.fillStyle = gradient;
        staticContext.fillRect(cx - gr * radius, cy - gr * radius, gr * 2 * radius, gr * 2 * radius);
      };
      glow(-1.02, -0.46, 1.55, 'rgba(158, 82, 44, 0.10)');
      glow(-1.85, 0.22, 1.85, 'rgba(118, 60, 36, 0.07)');
      glow(-0.74, -0.56, 0.78, 'rgba(255, 126, 66, 0.08)');

      // Fine dust grains.
      for (const particle of dustParticles) {
        staticContext.beginPath();
        staticContext.arc(particle.x, particle.y, particle.radius, 0, Math.PI * 2);
        staticContext.fillStyle = particle.cool
          ? '#8aa6ff'
          : particle.alpha > 0.4
            ? '#ffbe83'
            : '#e0955e';
        staticContext.globalAlpha = particle.alpha;
        staticContext.fill();
      }
      staticContext.globalAlpha = 1;

      // Warm halo climbing over the lit limb.
      const aura = staticContext.createRadialGradient(
        centerX - radius * 0.62,
        centerY - radius * 0.42,
        radius * 0.05,
        centerX - radius * 0.3,
        centerY - radius * 0.12,
        radius * 1.42,
      );
      aura.addColorStop(0, 'rgba(255, 140, 76, 0.30)');
      aura.addColorStop(0.3, 'rgba(198, 92, 48, 0.13)');
      aura.addColorStop(0.62, 'rgba(96, 42, 28, 0.05)');
      aura.addColorStop(1, 'rgba(5, 8, 14, 0)');
      staticContext.fillStyle = aura;
      staticContext.fillRect(
        centerX - radius * 1.7,
        centerY - radius * 1.7,
        radius * 3.4,
        radius * 3.4,
      );

      // The almost-black body occludes the dust and carries the contours.
      const body = staticContext.createRadialGradient(
        centerX - radius * 0.58,
        centerY - radius * 0.34,
        radius * 0.05,
        centerX,
        centerY,
        radius,
      );
      body.addColorStop(0, 'rgba(26, 17, 15, 1)');
      body.addColorStop(0.24, 'rgba(12, 10, 13, 1)');
      body.addColorStop(0.62, 'rgba(5, 7, 13, 1)');
      body.addColorStop(1, 'rgba(2, 4, 8, 1)');
      staticContext.beginPath();
      staticContext.arc(centerX, centerY, radius, 0, Math.PI * 2);
      staticContext.fillStyle = body;
      staticContext.fill();

    };

    // Contour rings, stroked in runs of constant quantized brightness so each
    // line fades naturally from its lit side into shadow. Re-rendered onto
    // their own layer whenever the orientation moves. Instead of a hard clip
    // circle, every line tapers smoothly to black as it rounds the limb —
    // that's what keeps the silhouette clean at any spin or tilt.
    const renderRings = () => {
      ringContext.clearRect(0, 0, width, height);
      ringContext.lineCap = 'round';
      ringContext.lineJoin = 'round';

      const SAMPLES = 380;
      for (const ring of rings) {
        let path: Path2D | null = null;
        let level = 0;
        let segments = 0;
        let previous: SurfacePoint | null = null;

        const flush = () => {
          if (path && segments > 0) {
            const { style, width: lineWidth } = RING_STYLES[level];
            ringContext.strokeStyle = style;
            ringContext.lineWidth = lineWidth;
            ringContext.stroke(path);
          }
          path = null;
          segments = 0;
        };

        for (let sample = 0; sample <= SAMPLES; sample += 1) {
          const lon = (sample / SAMPLES) * Math.PI * 2;
          const point = surface(ring.lat, lon);
          if (point.nz < -0.055) {
            flush();
            previous = null;
            continue;
          }
          const edge = Math.min(1, (point.nz + 0.055) / 0.085);
          const materialLon = lon - spin;
          const texture =
            1 +
            ring.a1 * Math.sin(2 * materialLon + ring.p1) +
            ring.a2 * Math.sin(5 * materialLon + ring.p2);
          const brightness = Math.min(
            1,
            Math.max(0, shade(point) * ring.gain * texture * (1 + energyLift * 0.22)) *
            edge *
            (2 - edge),
          );
          const quantized = Math.round(brightness * RING_LEVELS);
          // Let the very faintest level die out before the silhouette so the
          // line ends taper instead of leaving blunt stubs at the edge.
          if (quantized === 0 && edge < 0.6) {
            flush();
            previous = point;
            continue;
          }
          if (!path || quantized !== level) {
            flush();
            level = quantized;
            path = new Path2D();
            path.moveTo((previous ?? point).px, (previous ?? point).py);
            if (previous) {
              path.lineTo(point.px, point.py);
              segments += 1;
            }
          } else {
            path.lineTo(point.px, point.py);
            segments += 1;
          }
          previous = point;
        }
        flush();
      }

      // Soft blooms where the brightest traces meet the silhouette; they
      // follow the rim light as it swings with scroll.
      const bloom = (theta: number, size: number, color: string) => {
        const bx = centerX + Math.cos(theta) * radius * 0.99;
        const by = centerY - Math.sin(theta) * radius * 0.99;
        const gradient = ringContext.createRadialGradient(bx, by, 0, bx, by, size * radius);
        gradient.addColorStop(0, color);
        gradient.addColorStop(1, 'rgba(0, 0, 0, 0)');
        ringContext.fillStyle = gradient;
        ringContext.fillRect(
          bx - size * radius,
          by - size * radius,
          size * 2 * radius,
          size * 2 * radius,
        );
      };
      bloom(2.45 + lightSwing, 0.3, 'rgba(255, 150, 80, 0.17)');
      bloom(2.75 + lightSwing, 0.2, 'rgba(255, 140, 72, 0.11)');
      bloom(-0.88 + lightSwing, 0.24, 'rgba(255, 140, 70, 0.1)');

      renderedSpin = spin;
      renderedTiltX = tiltXNow;
      renderedTiltZ = tiltZNow;
      renderedSwing = lightSwing;
      renderedLift = energyLift;
    };

    const resize = () => {
      const bounds = canvas.getBoundingClientRect();
      const pixelRatio = Math.min(window.devicePixelRatio || 1, 2);
      width = Math.max(1, bounds.width);
      height = Math.max(1, bounds.height);
      canvas.width = Math.round(width * pixelRatio);
      canvas.height = Math.round(height * pixelRatio);
      staticLayer.width = canvas.width;
      staticLayer.height = canvas.height;
      ringLayer.width = canvas.width;
      ringLayer.height = canvas.height;
      context.setTransform(pixelRatio, 0, 0, pixelRatio, 0, 0);
      staticContext.setTransform(pixelRatio, 0, 0, pixelRatio, 0, 0);
      ringContext.setTransform(pixelRatio, 0, 0, pixelRatio, 0, 0);

      const compact = width < 760;
      centerX = compact ? width * 0.69 : width * 0.705;
      radius = compact
        ? Math.min(width * 0.42, height * 0.26)
        : Math.min(width * 0.25, height * 0.41);

      // Bottom-align the sphere: seat its lower cap a fixed gap above the bottom
      // edge and let any overflow fall off the top instead. The front rings are
      // lifted toward the bottom by perspective (the same 1 + 1/7.2 factor used
      // in surface()), so factor that lift in when anchoring the bottom margin.
      const BOTTOM_PADDING = 10;
      const PERSPECTIVE_LIFT = 1 + 1 / 7.2;
      // Compact screens don't bottom-align: the copy block owns the bottom of the
      // frame there, so the sphere is seated in the band between the header and
      // that copy rather than hugging the top edge (where it crowded the title).
      centerY = compact ? height * 0.48 : height - BOTTOM_PADDING - radius * PERSPECTIVE_LIFT;

      // const BOTTOM_PADDING = 10;
      // const PERSPECTIVE_LIFT = 1 + 1 / 7.2;
      // centerY = compact
      //   ? BOTTOM_PADDING + radius
      //   : height - BOTTOM_PADDING - radius * PERSPECTIVE_LIFT;

      rebuildScene();
      renderBackdrop();
      renderedSpin = Infinity;
    };

    const drawGlint = (
      x: number,
      y: number,
      size: number,
      cool: boolean,
      intensity: number,
      time: number,
      phase: number,
    ) => {
      const pulse = 0.82 + 0.18 * Math.sin(time * 0.0016 + phase);
      const glowRadius = size * pulse;
      const gradient = context.createRadialGradient(x, y, 0, x, y, glowRadius);
      gradient.addColorStop(0, `rgba(255, 250, 240, ${0.95 * intensity})`);
      gradient.addColorStop(
        0.16,
        cool ? `rgba(178, 205, 255, ${0.6 * intensity})` : `rgba(255, 196, 128, ${0.6 * intensity})`,
      );
      gradient.addColorStop(
        0.45,
        cool ? `rgba(96, 138, 255, ${0.18 * intensity})` : `rgba(255, 122, 52, ${0.18 * intensity})`,
      );
      gradient.addColorStop(1, 'rgba(0, 0, 0, 0)');
      context.fillStyle = gradient;
      context.fillRect(x - glowRadius, y - glowRadius, glowRadius * 2, glowRadius * 2);

      context.beginPath();
      context.arc(x, y, Math.min(1.6, size * 0.14), 0, Math.PI * 2);
      context.fillStyle = cool ? '#eef3ff' : '#fff6e8';
      context.globalAlpha = Math.min(1, intensity * pulse + 0.15);
      context.fill();
      context.globalAlpha = 1;
    };

    const drawArc = (arc: Arc, time: number, step: number) => {
      const palette = arc.cool ? COOL_ARC : WARM_ARC;
      // Arcs are rim lighting, so they stay screen-anchored — but they slide
      // a touch with scroll so the surface doesn't stream past a frozen glow.
      const wobble =
        Math.sin(time * 0.00022 + arc.phase) * 0.05 +
        step * 0.02 +
        (sectionProgress - 0.5) * 0.35;
      const start = arc.lon0 - arc.span / 2 + wobble;
      const SAMPLES = 34;
      const points: Array<{ x: number; y: number; envelope: number } | null> = [];
      for (let sample = 0; sample <= SAMPLES; sample += 1) {
        const point = surface(arc.lat, start + (sample / SAMPLES) * arc.span);
        points.push(
          point.nz < -0.02
            ? null
            : {
              x: point.px,
              y: point.py,
              envelope: Math.pow(Math.sin((sample / SAMPLES) * Math.PI), 1.4),
            },
        );
      }

      const flicker =
        (0.9 + 0.1 * Math.sin(time * 0.0011 + arc.phase * 2.3)) *
        arc.alpha *
        (1 + energyLift * 0.25);
      // Glow is layered strokes, not shadowBlur — far cheaper per frame.
      const passes: Array<[number, number, string]> = [
        [arc.width * 4.4, 0.1, palette.glow],
        [arc.width * 2.1, 0.3, palette.mid],
        [arc.width, 1, palette.core],
      ];
      for (const [lineWidth, passAlpha, style] of passes) {
        context.strokeStyle = style;
        context.lineWidth = lineWidth;
        for (let i = 0; i < SAMPLES; i += 1) {
          const a = points[i];
          const b = points[i + 1];
          if (!a || !b) continue;
          context.globalAlpha = Math.min(1, ((a.envelope + b.envelope) / 2) * passAlpha * flicker);
          context.beginPath();
          context.moveTo(a.x, a.y);
          context.lineTo(b.x, b.y);
          context.stroke();
        }
      }
      context.globalAlpha = 1;

      if (arc.glint > 0) {
        let peak: { x: number; y: number; envelope: number } | null = null;
        for (const point of points) {
          if (point && (!peak || point.envelope > peak.envelope)) peak = point;
        }
        if (peak) {
          drawGlint(peak.x, peak.y, 8 + arc.glint * 12, arc.cool, arc.glint, time, arc.phase);
        }
      }
    };

    // A spark with a comet tail traveling its contour ring — the "laser"
    // pattern from the reference: a near-white head that trails off through
    // the palette's mid tone into a soft glow, fading out at the limb.
    const drawComet = (comet: Comet, time: number) => {
      const palette = comet.cool ? COOL_ARC : WARM_ARC;
      const SAMPLES = 24;
      const points: Array<{ x: number; y: number; vis: number } | null> = [];
      for (let sample = 0; sample <= SAMPLES; sample += 1) {
        // + spin so the globe's rotation carries the whole streak; the sample
        // sweep from (lon - tail) to lon lays the tail out behind the head.
        const lon = comet.lon + spin - comet.tail + (sample / SAMPLES) * comet.tail;
        const point = surface(comet.lat, lon);
        points.push(
          point.nz < -0.02
            ? null
            : {
              x: point.px,
              y: point.py,
              vis: Math.min(1, Math.max(0, (point.nz + 0.02) / 0.16)),
            },
        );
      }

      // Head-to-tail falloff per pass: the core hugs the head, the glow
      // stretches down the whole tail — same layered-stroke trick as drawArc.
      const boost = comet.alpha * (0.55 + scrollEnergy * 0.6);
      const passes: Array<[number, number, number, string]> = [
        [comet.width * 4.2, 0.12, 1.2, palette.glow],
        [comet.width * 2.0, 0.32, 2.0, palette.mid],
        [comet.width * 0.95, 0.85, 3.2, palette.core],
      ];
      for (const [lineWidth, passAlpha, falloff, style] of passes) {
        context.strokeStyle = style;
        context.lineWidth = lineWidth;
        for (let i = 0; i < SAMPLES; i += 1) {
          const a = points[i];
          const b = points[i + 1];
          if (!a || !b) continue;
          const alpha =
            Math.pow((i + 0.5) / SAMPLES, falloff) * passAlpha * boost * Math.min(a.vis, b.vis);
          if (alpha < 0.012) continue;
          context.globalAlpha = Math.min(1, alpha);
          context.beginPath();
          context.moveTo(a.x, a.y);
          context.lineTo(b.x, b.y);
          context.stroke();
        }
      }
      context.globalAlpha = 1;

      const head = points[SAMPLES];
      if (head && head.vis > 0.25) {
        drawGlint(
          head.x,
          head.y,
          5 + comet.width * 4 + scrollEnergy * 7,
          comet.cool,
          (0.32 + scrollEnergy * 0.45) * comet.alpha * head.vis,
          time,
          comet.lon,
        );
      }
    };

    const draw = (time: number) => {
      const dt = lastTime ? Math.min(0.05, (time - lastTime) / 1000) : 0.016;
      lastTime = time;

      // Scroll feeds the sparks: scrubbing drags them directly along their
      // rings, and recent movement leaves a decaying surge of speed + heat.
      const progress = Number(canvas.dataset.progress ?? 0);
      const deltaProgress = lastProgress < 0 ? 0 : progress - lastProgress;
      lastProgress = progress;
      sectionProgress = progress;
      scrollEnergy = Math.min(
        1,
        scrollEnergy * Math.exp(-dt * 2.4) + Math.abs(deltaProgress) * 26,
      );

      // The globe itself moves: scrubbing rotates it directly (~126° across
      // the section), recent scrolling leaves a decaying spin surge on top of
      // a slow idle drift, and the whole sphere tumbles a few degrees while
      // the rim light swings across the limb as the steps advance.
      const spinDelta =
        dt * (0.022 + scrollEnergy * 0.4) + deltaProgress * 2.2;
      spin += spinDelta;
      energyLift = scrollEnergy;
      lightSwing = (progress - 0.5) * 0.18;
      tiltXNow =
        TILT_X + Math.sin(progress * Math.PI) * 0.045 + Math.sin(time * 0.00005 + 1.3) * 0.009;
      tiltZNow = TILT_Z + (progress - 0.5) * 0.09 + Math.sin(time * 0.00004) * 0.007;
      setOrientation(tiltXNow, tiltZNow);

      // Re-render the ring layer only when the sphere has visibly moved.
      if (
        Math.abs(spin - renderedSpin) > 0.0015 ||
        Math.abs(tiltXNow - renderedTiltX) > 0.0006 ||
        Math.abs(tiltZNow - renderedTiltZ) > 0.0006 ||
        Math.abs(lightSwing - renderedSwing) > 0.004 ||
        Math.abs(energyLift - renderedLift) > 0.02
      ) {
        renderRings();
      }

      context.clearRect(0, 0, width, height);

      context.save();
      context.setTransform(1, 0, 0, 1, 0, 0);
      context.drawImage(staticLayer, 0, 0);
      context.drawImage(ringLayer, 0, 0);
      context.restore();

      context.lineCap = 'round';
      context.lineJoin = 'round';

      // Sparse twinkling star field around the sphere.
      for (const star of stars) {
        const x = star.x * width;
        const y = star.y * height;
        if (Math.hypot(x - centerX, y - centerY) < radius + 2) continue;
        const proximity = Math.max(0, 1 - Math.abs(x - centerX) / (radius * 2.7));
        const leftFade = Math.max(0, Math.min(1, (x / width - 0.24) / 0.2));
        if (proximity <= 0 || leftFade <= 0) continue;
        const twinkle = 0.7 + Math.sin(time * 0.0012 + star.phase) * 0.3;
        context.beginPath();
        context.arc(x, y, star.radius, 0, Math.PI * 2);
        context.fillStyle = star.warm ? '#ff9a62' : '#7199ff';
        context.globalAlpha = star.alpha * twinkle * proximity * leftFade;
        context.fill();
      }

      // Animated grains inside the dust banks.
      for (const spark of dustSparks) {
        const twinkle = 0.55 + Math.sin(time * 0.0014 + spark.phase) * 0.45;
        context.beginPath();
        context.arc(spark.x, spark.y, spark.radius, 0, Math.PI * 2);
        context.fillStyle = spark.cool ? '#9db4ff' : '#ffc48f';
        context.globalAlpha = spark.alpha * Math.max(0.08, twinkle);
        context.fill();
      }
      context.globalAlpha = 1;

      const activeStep = Number(canvas.dataset.step ?? 0);
      for (const arc of arcs) drawArc(arc, time, activeStep);

      for (const comet of comets) {
        // Only the comet's own orbital drift advances its material longitude;
        // all scroll-driven motion comes through `spin` in drawComet, so the
        // sparks rotate locked to the surface (and to each other) instead of
        // sliding across the contour lines. A little scroll energy quickens
        // the drift so they feel alive, but never enough to break the lock.
        comet.lon += comet.speed * dt * (1 + scrollEnergy * 0.6);
        if (comet.lon > Math.PI * 2) comet.lon -= Math.PI * 2;
        if (comet.lon < 0) comet.lon += Math.PI * 2;
        drawComet(comet, time);
      }

      // Pinpoint sparkles riding the lit contours — reprojected every frame
      // so they rotate with the surface and fade out at the limb.
      for (const node of sparkNodes) {
        const point = surface(node.lat, node.lon + spin);
        if (point.nz < 0.04) continue;
        const vis = Math.min(1, (point.nz - 0.04) / 0.1);
        const twinkle = 0.35 + 0.65 * Math.abs(Math.sin(time * 0.0009 + node.phase));
        context.beginPath();
        context.arc(point.px, point.py, node.radius, 0, Math.PI * 2);
        context.fillStyle = node.cool ? '#c9d9ff' : '#ffdcae';
        context.globalAlpha = (0.25 + shade(point) * 0.6) * twinkle * vis;
        context.fill();
      }
      context.globalAlpha = 1;

      if (!reducedMotion) animationFrame = requestAnimationFrame(draw);
    };

    const resizeObserver = new ResizeObserver(() => {
      resize();
      if (reducedMotion) draw(0);
    });
    resizeObserver.observe(canvas);
    resize();
    draw(0);

    return () => {
      resizeObserver.disconnect();
      cancelAnimationFrame(animationFrame);
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      className="rh-loop-planet"
      data-step={active}
      aria-hidden="true"
    />
  );
}

export function RhapsodyLoop() {
  const sectionRef = useRef<HTMLElement>(null);
  const barRef = useRef<HTMLSpanElement>(null);
  const [active, setActive] = useState(0);

  useEffect(() => {
    const section = sectionRef.current;
    if (!section) return;

    const planet = section.querySelector<HTMLCanvasElement>('.rh-loop-planet');

    let raf = 0;
    const update = () => {
      raf = 0;
      const rect = section.getBoundingClientRect();
      const scrollable = rect.height - window.innerHeight;
      if (scrollable <= 0) return;
      const p = Math.min(1, Math.max(0, -rect.top / scrollable));
      if (barRef.current) barRef.current.style.transform = `scaleX(${p})`;
      if (planet) planet.dataset.progress = p.toFixed(4);
      setActive(Math.min(STEPS.length - 1, Math.floor(p * STEPS.length)));
    };

    const onScroll = () => {
      if (!raf) raf = requestAnimationFrame(update);
    };

    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    update();

    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
      if (raf) cancelAnimationFrame(raf);
    };
  }, []);

  return (
    <section
      className="rh-loop"
      id="loop"
      ref={sectionRef}
      style={{ height: `${(STEPS.length + 1) * 92}vh` }}
    >
      <div className="rh-loop-sticky">
        {/* procedural orbital planet — replaces the background video */}
        <div className="rh-loop-media" aria-hidden="true">
          <OrbitalPlanet active={active} />
          <div className="rh-loop-scrim" />
        </div>

        {/* quiet header, top-left */}
        <div className="rh-loop-top">
          <p className="rh-kicker">The self-correcting loop</p>
          <h2 className="rh-loop-title">
            Reasoning proposes.
            <br />
            <em> Verification disposes.</em>
          </h2>
        </div>

        {/* the active stage — cinematic copy, lower-left */}
        <div className="rh-loop-copy">
          <span className="rh-loop-count" aria-hidden="true">
            {String(active + 1).padStart(2, '0')}
            <i />
            {String(STEPS.length).padStart(2, '0')}
          </span>
          <div className="rh-loop-stage">
            {STEPS.map((s, i) => (
              <div className={`rh-loop-step ${i === active ? 'on' : ''}`} key={s.label}>
                <h3>{s.label}</h3>
                <p>{s.body}</p>
                <p className="detail">{s.detail}</p>
              </div>
            ))}
          </div>
        </div>

        {/* step rail, right edge */}
        <ol className="rh-loop-rail" aria-hidden="true">
          {STEPS.map((s, i) => (
            <li
              className={`${i === active ? 'on' : ''} ${i < active ? 'done' : ''}`}
              key={s.label}
            >
              <span className="idx">{String(i + 1).padStart(2, '0')}</span>
              <span className="lbl">{s.label}</span>
            </li>
          ))}
        </ol>

        {/* scrub progress */}
        <div className="rh-loop-bar" aria-hidden="true">
          <span ref={barRef} />
        </div>
      </div>
    </section>
  );
}
